import { useEffect, useMemo, useRef, useState } from 'react';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { Routes, QUOTE_LABEL, WEEKS_SESSION_KEY_LABEL, WEEKS_UNITS_SESSION_KEY_LABEL } from '@share/constants';
import { getWeeksReviewBook, IWeeksReviewBookState, weeksReviewBookAction } from '@store/slices';
import { ICondosStaticState } from '@share/store/slices';
import { BookingErrorsEnum, ISessionKey } from '@share/common-types';
import {
  getSessionObj,
  getTimeout,
  UrlUtils,
  RootState,
  runStripeExpressResumeFromQuery,
  mergeStripeExpressResumeSearch,
  computeCreditLineChargeTotal,
  getSelectedCurrency,
  isCreditLineCheckoutAccount,
  redirectToCheckoutFromStripeResume,
  consumeStripeResumeErrorFromLocation,
} from '@share/utils';
import { store } from '@store';
import { CreditLineCheckoutPageBanner, StripeResumeError } from '@share/components';
import { IInsuranceState, InsuranceSelection } from '@store/slices';
import { Notification, SkeletonReviewBook } from '@components';
import { WeeksReviewBook } from '../weeks-review-book';
import { WeeksUnitInfo } from '../weeks-unit-info';
import { ILoginState } from '@share/store/slices';
import { NULL_VALUE } from '@constants';

import './style.scss';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IMapStateToProps {
  weeksReviewBookStore: IWeeksReviewBookState;
  condosStaticStore: ICondosStaticState;
  loginStore: ILoginState;
  insuranceStore: IInsuranceState;
}

interface IMapDispatchToProps {
  resetBooking: () => void;
  getWeeksReviewBook: (
    numberId: number,
    packageId: string,
    sessionKey: ISessionKey,
    isGetCoupon?: false,
    quote?: string
  ) => void;
  setLoadingWeeksBook: (loadingBook: boolean) => void;
  setWeeksExpiredSession: (expiredSession: boolean) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  isStripe?: boolean;
}

const ZERO = 0;
const FOUR = 4;
const FIVE = 5;

function WeeksReviewBookWrapperComponent(props: IProps) {
  
  const { history, weeksReviewBookStore, condosStaticStore, resetBooking, getWeeksReviewBook, isStripe, insuranceStore, loginStore } = props;
  const {
    weeksBookingSummary,
    loading,
    error,
    expiredSession,
    isExternalError,
    bookingComplete,
    isBookingPending,
    bookingErrorCode,
    loadingBooking,
    isSavingsMismatch,
  } = weeksReviewBookStore;
  const { condosCountriesCode } = condosStaticStore;
  const { account, userWalletData } = loginStore;
  const { selection, insurance } = insuranceStore;
  const { selectedWeeksReviewClientCash, rewardId } = weeksReviewBookStore;

  const historySplit = useMemo(() => history.location.pathname.split('/'), [history.location.pathname]);
  const propertyId = Number(historySplit[FOUR]);
  const packageId = historySplit[FIVE];

  const loadingRef = useRef(loading);
  const errorRef = useRef(error);
  const [stripeResumeError, setStripeResumeError] = useState<string | null>(null);
  const [stripeNoResume, setStripeNoResume] = useState<boolean>(false);
  
  const getSessionId = () => {
    const values = UrlUtils.getValues();
    return getSessionObj(
      values[WEEKS_UNITS_SESSION_KEY_LABEL] as ISessionKey,
      values[WEEKS_SESSION_KEY_LABEL] as ISessionKey,
    );
  };

  const onExpiredSession = (sessionData: ISessionKey) => {
    const expireDate = getTimeout(sessionData.expireDate);
    const timeout = expireDate < ZERO ? 0 : expireDate;

    setTimeout(() => {
      props.setWeeksExpiredSession(true);
    }, timeout);
  };

  useEffect(() => {
    const values = UrlUtils.getValues();
    const sessionKey = getSessionId();

    let quote;
    if (values[QUOTE_LABEL]) {
      quote = (values[QUOTE_LABEL] as string);
    }

    if (isStripe) {
      const resumeSearch = mergeStripeExpressResumeSearch(history.location.search, sessionKey);
      void runStripeExpressResumeFromQuery({
        dispatch: store.dispatch,
        getState: store.getState as () => RootState,
        history,
        pathname: history.location.pathname,
        search: resumeSearch,
        vertical: 'weeks',
      }).then((res) => {
        if (res.error && res.isResumeError) {
          // Resume call itself failed → hand off to the regular checkout page (which has the full
          // booking context) and let it surface the error inline as a normal checkout error.
          redirectToCheckoutFromStripeResume(history, res.error);
        } else if (res.error) {
          // Pre-resume failure (e.g., missing session) — show the full-page StripeResumeError.
          setStripeResumeError(res.error);
        } else if (!res.ran) {
          // No bookingId in the URL (e.g., F5 after a successful booking, once params were stripped).
          setStripeNoResume(true);
        }
      });
    } else {
      // Surface a resume error redirected here from the /stripe/weeks/... page (if any). Weeks shows
      // booking failures via `bookingErrorCode`, so flag a generic "something went wrong" code and
      // store the original message in `errorBooking` for any downstream consumers.
      const stripeResumeErrorMsg = consumeStripeResumeErrorFromLocation(history);
      if (stripeResumeErrorMsg) {
        store.dispatch(weeksReviewBookAction.setWeeksErrorBooking(stripeResumeErrorMsg));
        store.dispatch(weeksReviewBookAction.setBookingErrorCodeError(BookingErrorsEnum.General));
      }
      getWeeksReviewBook(propertyId, packageId, sessionKey, false, quote);
    }
    onExpiredSession(sessionKey);
    window.scrollTo(ZERO, ZERO);

    return () => {
      resetBooking();
    }
    // getWeeksReviewBook / resetBooking from connect are stable; avoid re-running on identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount when route or stripe mode changes
  }, [isStripe, propertyId, packageId, history.location.pathname, history.location.search]);

  useEffect(() => {
    if (loadingRef.current !== loading && !loading && errorRef.current === BookingErrorsEnum.BookingSummeryInformationFailed) {
      history.push(`/${account?.name}${Routes.WeeksDetails}/${propertyId}${location.search}`);
      location.reload();
    }
    loadingRef.current = loading;
  },[loading]);

  useEffect(() => {
    errorRef.current = error;
  },[error]);

  const isNotification = bookingComplete || loadingBooking || isBookingPending || bookingErrorCode === BookingErrorsEnum.SoldOut;
  const isUnavailable = bookingErrorCode === BookingErrorsEnum.SoldOut || bookingErrorCode === BookingErrorsEnum.RoomsUnavailable;
  const displayNotification = expiredSession || isExternalError || !condosCountriesCode || isUnavailable;
  const sessionKey = getSessionId();

  const { payNowPrice, maxRewardWalletAmount } = weeksBookingSummary?.bookingCard?.bookingPrice ?? {};
  const convertionRate = userWalletData?.convertionRate ?? 1;
  const insuranceAmount =
    selection === InsuranceSelection.YES && insurance?.productDetails?.price
      ? insurance.productDetails.price
      : 0;
  const rewardDeduction = rewardId && maxRewardWalletAmount ? maxRewardWalletAmount : 0;
  const creditLineBookingAmount =
    payNowPrice != null
      ? computeCreditLineChargeTotal({
          payOrBalance: payNowPrice,
          clientCashPoints: selectedWeeksReviewClientCash,
          conversionRate: convertionRate,
          insuranceAmount,
          rewardDeduction,
        })
      : 0;
  const showCreditLineBanner =
    isCreditLineCheckoutAccount(account) &&
    !isNotification &&
    !!weeksBookingSummary &&
    !loading &&
    !isUnavailable;

  if (isStripe && stripeResumeError) {
    return (
      <div className="review-book-wrapper print">
        <StripeResumeError />
      </div>
    );
  }

  if (isStripe && stripeNoResume) {
    return (
      <div className="review-book-wrapper print">
        <StripeResumeError mode="go-to-search" />
      </div>
    );
  }

  /** On stripe-return pages, the wrapper has no booking data yet, so render a skeleton until the
   * resume settles (success → BookingConfirmed; failure → early-returned `<StripeResumeError />`). */
  const showStripeSkeleton = !!isStripe && !bookingComplete;

  return displayNotification ? (
    <Notification
      expiredSession={expiredSession}
      isExternalError={isExternalError || !condosCountriesCode}
      isFromCondo={true}
      isSavingsMismatch={isSavingsMismatch}
      errorCode={isUnavailable ? bookingErrorCode : NULL_VALUE}
      sessionId={sessionKey?.value || ''}
    />) : (
      <div className="review-book-wrapper print">
      {!isNotification && !isStripe && (
        <div className="review-book-wrapper__header print">
          <div className="review-book-wrapper__back-link">
            <Link to={`/${account?.name}${Routes.WeeksDetails}/${propertyId}${location.search}`}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
              <FormattedMessage id="back.to.condo" />
            </Link>
          </div>
          <h4 className="review-book-wrapper__title secondary-font">
            <FormattedMessage id="review.book" />
          </h4>
          <p className="review-book-wrapper__description">
            <FormattedMessage id="enter.your.details" />
          </p>
        </div>
      )}

      {showCreditLineBanner ? (
        <CreditLineCheckoutPageBanner
          bookingAmount={creditLineBookingAmount}
          currency={getSelectedCurrency(account)}
        />
      ) : null}
      
      {(loading || showStripeSkeleton) ? <SkeletonReviewBook /> : null}
      {/* On stripe-return pages we only render the inner WeeksReviewBook once the booking
          confirmed successfully — otherwise the form (and its inline error warning) would briefly
          flash before the resume-failure redirect navigates away (force-full-stripe flows can
          leave a `weeksBookingSummary` in the store even when the resume call errored out). */}
      {(isStripe ? bookingComplete : !!weeksBookingSummary) && !loading && condosCountriesCode ? (
        <div className="review-book-wrapper__content">
          <WeeksReviewBook />
          <WeeksUnitInfo />
        </div>
      ) : null}
      </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    weeksReviewBookStore: state.weeksReviewBookStore,

    condosStaticStore: state.condosStaticStore,
    loginStore: state.loginStore,
    insuranceStore: state.insuranceStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getWeeksReviewBook,
  setLoadingWeeksBook: weeksReviewBookAction.setLoadingWeeksBook,
  setWeeksExpiredSession: weeksReviewBookAction.setWeeksExpiredSession,
  resetBooking: weeksReviewBookAction.weekBookingResetState,
};

export const WeeksReviewBookWrapper = connect(mapStateToProps, mapDispatchToProps)(withRouter(WeeksReviewBookWrapperComponent));
